import { useEffect, useMemo, useState } from "react";
import { useLang } from "../i18n";
import { type Lang, todayIn } from "../lib/dates";
import { useSyncState } from "../store";

export interface Prefs {
  tz: string;
  lang: Lang;
  today: string;
  nowTime: string;
  startDay: number;
  timeFormat: number;
  dateFormat: number;
}

function localTime(tz: string): string {
  try {
    return new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: false })
      .format(new Date())
      .replace(/^24/, "00");
  } catch {
    return new Date().toTimeString().slice(0, 5);
  }
}

/** User date/time preferences plus a `today` that rolls over at local midnight. */
export function usePrefs(): Prefs {
  const user = useSyncState().user;
  const lang = useLang();
  const tz = user?.tz_info.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 60_000);
    return () => clearInterval(t);
  }, []);
  // biome-ignore lint/correctness/useExhaustiveDependencies: tick forces a minute refresh
  return useMemo(
    () => ({
      tz,
      lang,
      today: todayIn(tz),
      nowTime: localTime(tz),
      startDay: user?.start_day ?? 1,
      timeFormat: user?.time_format ?? 0,
      dateFormat: user?.date_format ?? 0,
    }),
    [tz, lang, user?.start_day, user?.time_format, user?.date_format, tick],
  );
}
